import AuthenticationMethodBase from "@/managers/authentication/authenticationMethodBase"
import Notifications from "@/managers/session/notifications"
import Token from "@/managers/authentication/token"

export default abstract class JwtAuthenticationMethod extends AuthenticationMethodBase {
	public static readonly expiredEvent = "expired"
	private readonly storage: Storage
	private readonly storageKey: string
	private readonly isTokenSingleUse: boolean

	private expirationTimeout: number | null = null
	private expirationTimeoutToken: string | null = null
	private _loginPromise: Promise<void> | null = null

	public get loginPromise(): Promise<void> | null {
		return this._loginPromise
	}

	private get storedToken(): string | null {
		return this.storage.getItem(this.storageKey)
	}
	private set storedToken(value: string | null) {
		if (value !== null)
			this.storage.setItem(this.storageKey, value)
		else
			this.storage.removeItem(this.storageKey)
	}

	protected constructor(storage: Storage, storageKey: string, isTokenSingleUse: boolean) {
		super(null)

		this.storage = storage
		this.storageKey = storageKey
		this.isTokenSingleUse = isTokenSingleUse

		this.checkToken()
		this.listenForStorage()
	}

	public login(): Promise<void> {
		if (!this.canLogin)
			throw new Error("Can't login now")

		if (this._loginPromise !== null)
			return this._loginPromise

		this._loginPromise = this.lockToken(async () => {
			const token = this.storedToken
			if (token === null) {
				this.setCanLogin(false)
				throw new Error("No saved token")
			}

			if (this.isTokenSingleUse) {
				Notifications?.debug(`Clearing single use token: ${token.slice(-5)}`)
				this.storedToken = null
				this.clearTimeOut()
			}

			Notifications?.debug(`Trying to login with ${this.storageKey}`)

			this.setLoggingIn()
			try {
				const newRawToken = await this.loginWithToken(token)

				if (newRawToken !== null) {
					const newToken = new Token(newRawToken)
					Notifications?.debug(`Setting new token: ${newToken.shortRaw}`)
					this.storedToken = newToken.raw
					this.updateTimeOut(newToken)
				}

				this.setCanLogin(true)
			} catch (error: any) {
				Notifications?.debug(`Login failed for ${this.storageKey} with: ${error?.message}`)

				if (!this.isTokenSingleUse) {
					this.storedToken = null
					this.clearTimeOut()
				}

				this.setCanLogin(false)
				throw error
			}
		})

		return this._loginPromise
			.finally(() => (this._loginPromise = null))
	}

	public async clearInvalidToken(token: string): Promise<void> {
		if (token === this.storedToken)
			await this.clear(token)
	}

	public async clearAndDelete(): Promise<void> {
		const token = this.storedToken

		if (token === null)
			return

		this.deleteToken(token)
		return this.clear(token)
	}

	public async clear(token: string | null = null): Promise<void> {
		Notifications?.debug(`Clearing token: ${this.storageKey}`)

		const currentToken = this.storedToken
		if ((token !== null && token !== currentToken) || currentToken === null)
			return

		await this.lockToken(async () => this.clearToken(token))
	}

	protected abstract loginWithToken(token: string): Promise<string | null>
	protected deleteToken(token: string): void {}

	protected handleTokenExpiry(token: Token): Promise<void> {
		return this.clear(token.raw)
	}

	protected validateAndSetToken(token: Token): Promise<boolean> {
		return this.lockToken(async () => {
			if (this.storedToken === token.raw)
				return true

			const expired = token.calculateIsExpiredNow()

			Notifications?.debug(`Setting new ${this.storageKey}, expired: ${expired}, MFA: ${token.isMultiFactorAuthenticated}, MFA satisfied: ${token.isMultifactorSatisfied}`)

			if (expired || !token.isMultifactorSatisfied)
				return false

			this.storedToken = token.raw
			this.updateTimeOut(token)
			this.setCanLogin(true)
			return true
		})
	}

	private checkToken(): void {
		const rawToken = this.storedToken
		if (rawToken !== null) {
			let token: Token | null = null

			try {
				token = new Token(rawToken)
			} catch (error: any) {
				if (Notifications !== undefined) // Handle circular dependency
					Notifications.warning("Failed to parse token: " + error.message)
				else
					setTimeout(() => Notifications.warning("Failed to parse token: " + error.message))
				this.setCanLogin(false)
				this.clear(rawToken)
				return
			}

			if (token.calculateIsExpiredNow()) {
				this.setCanLogin(false)
				this.clear(rawToken)
			}
			else {
				this.updateTimeOut(token)
				this.setCanLogin(true)
			}
		} else
			this.setCanLogin(false)
	}

	private listenForStorage(): void {
		window.addEventListener("storage", (event: StorageEvent) => {
			if (event.storageArea !== this.storage)
				return

			if (event.key === this.storageKey) {
				const hasValue = event.newValue !== null
				this.setCanLogin(hasValue)
				if (hasValue)
					this.updateTimeOut(new Token(event.newValue!))
			} else if (event.key === null)
				this.setCanLogin(false)
		})
	}

	private updateTimeOut(token: Token): void {
		this.clearTimeOut(token.raw)

		if (token.calculateIsExpiredNow())
			return

		this.expirationTimeoutToken = token.raw
		this.expirationTimeout = window.setTimeout(
			async () => {
				this.expirationTimeout = null
				this.expirationTimeoutToken = null
				await this.checkAndHandleExpired(token)
			},
			token.getMillisecondsUntilExpiration()
		)
	}

	private async checkAndHandleExpired(token: Token): Promise<void> {
		if (this.storedToken !== token.raw) {
			const currentToken = this.storedToken
			if (currentToken !== null) {
				try {
					const newToken = new Token(currentToken)
					this.updateTimeOut(newToken)
					return
				} catch (error: any) {
					Notifications?.warning("Failed to parse current token when rescheduling expiry: " + error.message)
				}
			}
			return
		}

		Notifications?.debug(`Triggering token expiration: ${token.shortRaw}`)
		await this.handleTokenExpiry(token)
		this.dispatchEvent(new Event(JwtAuthenticationMethod.expiredEvent))
	}

	private clearTimeOut(token: string | null = null): void {
		if (this.expirationTimeout !== null && (token === null || this.expirationTimeoutToken === token)) {
			clearTimeout(this.expirationTimeout)
			this.expirationTimeout = null
			this.expirationTimeoutToken = null
		}
	}

	private clearToken(token: string | null = null): boolean {
		const currentToken = this.storedToken

		if ((token !== null && token !== currentToken) || currentToken === null)
			return false

		Notifications?.debug(`${this.storageKey} cleared: ${currentToken.slice(-5)}`)
		this.storedToken = null
		this.clearTimeOut()
		this.setCanLogin(false)
		return true
	}

	private lockToken<T>(call: () => Promise<T>): Promise<T> {
		if (navigator.locks !== undefined)
			return navigator.locks.request(this.storageKey, call)
		else
			return call()
	}
}

export {Token}
